import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../utils/AuthContext';
import {
  createEmptyProgram,
  getProgramTabLabel,
  getProgramsSnapshot,
  normalizeProgramsFromApi
} from '../../utils/programUtils';
import ProgramDocumentsEditor from './ProgramDocumentsEditor';
import RegistrationFormEditor from './RegistrationFormEditor';
import HomepageIntroEditor from './HomepageIntroEditor';
import {
  getFormFieldsSnapshot,
  getRegistrationFieldValue,
  normalizeFormFieldsFromApi
} from '../../utils/registrationFormUtils';
import {
  emptyHomepageIntro,
  getHomepageIntroSnapshot,
  normalizeHomepageIntro
} from '../../utils/homepageIntroUtils';
import { motion, AnimatePresence } from 'framer-motion';

const escapeCsvCell = (value) => {
  const str = String(value ?? '');
  if (/[",\n\r;]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const sanitizeFilename = (value) => String(value ?? 'forum')
  .trim()
  .replace(/[<>:"/\\|?*]+/g, '')
  .replace(/\s+/g, '-')
  .slice(0, 60) || 'forum';

const FALLBACK_EXPORT_FIELDS = [
  { key: 'lastName', label: 'Фамилия' },
  { key: 'firstName', label: 'Имя' },
  { key: 'middleName', label: 'Отчество' },
  { key: 'organization', label: 'Организация' },
  { key: 'position', label: 'Должность' },
  { key: 'region', label: 'Субъект РФ' },
  { key: 'phone', label: 'Телефон' },
  { key: 'email', label: 'Email' }
];

const downloadRegistrationsExcel = (rows, filename, fields = []) => {
  const exportFields = fields.length ? fields : FALLBACK_EXPORT_FIELDS;
  const headers = ['ID', ...exportFields.map((field) => field.label), 'Дата регистрации'];
  const separator = ';';
  const contentRows = rows.map((reg) => [
    reg.id,
    ...exportFields.map((field) => getRegistrationFieldValue(reg, field)),
    reg.timestamp
  ]);

  const csvContent = `\uFEFF${headers.map(escapeCsvCell).join(separator)}\n${contentRows.map((row) => row.map(escapeCsvCell).join(separator)).join('\n')}`;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const RegistrationsTable = ({ registrations, fields = [], emptyMessage }) => {
  const columns = fields.length ? fields : FALLBACK_EXPORT_FIELDS;

  return (
    <div className="registrations-list">
      {registrations.length === 0 ? (
        <div className="admin-empty-state">
          <p>{emptyMessage || 'Пока никто не зарегистрировался'}</p>
        </div>
      ) : (
        <table className="registrations-table">
          <thead>
            <tr>
              <th>ID</th>
              {columns.map((field) => (
                <th key={field.key}>{field.label}</th>
              ))}
              <th>Дата</th>
            </tr>
          </thead>
          <tbody>
            {registrations.map((reg) => (
              <tr key={reg.id}>
                <td>{reg.id}</td>
                {columns.map((field) => (
                  <td key={`${reg.id}-${field.key}`}>{getRegistrationFieldValue(reg, field)}</td>
                ))}
                <td>
                  {new Date(reg.timestamp).toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const RegistrationsForumPanel = ({ program, registrations, programIndex, fields }) => {
  const forumLabel = getProgramTabLabel(program, programIndex);

  const handleDownload = () => {
    downloadRegistrationsExcel(
      registrations,
      `uchastniki-${sanitizeFilename(forumLabel)}.csv`,
      fields
    );
  };

  return (
    <div className="registration-forum-panel">
      <div className="admin-toolbar admin-toolbar--compact">
        <button type="button" className="admin-action-btn admin-action-btn--success" onClick={handleDownload}>
          Скачать Excel — {forumLabel}
        </button>
        <span className="admin-toolbar__count">Записей: {registrations.length}</span>
      </div>
      <RegistrationsTable
        registrations={registrations}
        fields={fields}
        emptyMessage="Пока никто не зарегистрировался на этот форум"
      />
    </div>
  );
};

const pluralForums = (count) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 14) return 'форумов';
  if (mod10 === 1) return 'форум';
  if (mod10 >= 2 && mod10 <= 4) return 'форума';
  return 'форумов';
};

const pluralScheduleRows = (count) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 14) return 'строк';
  if (mod10 === 1) return 'строка';
  if (mod10 >= 2 && mod10 <= 4) return 'строки';
  return 'строк';
};

const sectionCollapseTransition = {
  height: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
  opacity: { duration: 0.28, ease: 'easeInOut' }
};

const SectionHeader = ({ title, subtitle, isOpen, onToggle, actions }) => (
  <div className={`admin-section-card__header ${isOpen ? 'is-open' : ''}`}>
    <button
      type="button"
      className="admin-section-card__toggle"
      aria-expanded={isOpen}
      onClick={onToggle}
    >
      <div className="admin-section-card__title-wrap">
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
    </button>
    {actions ? <div className="admin-section-card__actions">{actions}</div> : null}
    <button
      type="button"
      className="admin-section-card__chevron-btn"
      aria-expanded={isOpen}
      aria-label={isOpen ? 'Свернуть' : 'Развернуть'}
      onClick={onToggle}
    >
      <span className={`admin-section-card__chevron ${isOpen ? 'is-open' : ''}`} aria-hidden="true" />
    </button>
  </div>
);

const CollapsibleSectionBody = ({ isOpen, children }) => (
  <motion.div
    className="admin-section-card__collapse"
    initial={false}
    animate={{
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? 1 : 0
    }}
    transition={sectionCollapseTransition}
  >
    <div className="admin-section-card__body">
      {children}
    </div>
  </motion.div>
);

const AdminPanel = () => {
  const { isRegistrationCompleted, setIsRegistrationCompleted, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);

  const [isRegistrationCompleteClicked, setIsRegistrationCompleteClicked] = useState(false);
  const [isRegistrationReopenClicked, setIsRegistrationReopenClicked] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isReopenPopupVisible, setIsReopenPopupVisible] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeForumTab, setActiveForumTab] = useState(0);
  const [formFields, setFormFields] = useState([]);
  const [programsEnabled, setProgramsEnabled] = useState(true);
  const [isTogglingPrograms, setIsTogglingPrograms] = useState(false);
  const [activeSections, setActiveSections] = useState({
    intro: true,
    programs: true,
    form: true,
    registrations: false
  });
  const [homepageIntro, setHomepageIntro] = useState(emptyHomepageIntro());
  const [savedIntroSnapshot, setSavedIntroSnapshot] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [savedProgramsSnapshot, setSavedProgramsSnapshot] = useState('');
  const [savedFormSnapshot, setSavedFormSnapshot] = useState('');
  const [leaveConfirmAction, setLeaveConfirmAction] = useState(null);

  useEffect(() => {
    setIsLoading(true);

    Promise.all([
      axios.get('/api/programs'),
      axios.get('/registrations'),
      axios.get('/api/registration-form'),
      axios.get('/api/homepage-intro')
    ])
      .then(([programsResponse, registrationsResponse, formResponse, introResponse]) => {
        const loadedPrograms = normalizeProgramsFromApi(programsResponse.data?.sections || []);
        const nextPrograms = loadedPrograms.length ? loadedPrograms : [createEmptyProgram()];
        const loadedFields = normalizeFormFieldsFromApi(formResponse.data?.fields || []);
        const loadedIntro = normalizeHomepageIntro(introResponse.data);
        setPrograms(nextPrograms);
        setSavedProgramsSnapshot(getProgramsSnapshot(nextPrograms));
        setProgramsEnabled(programsResponse.data?.isEnabled !== false);
        setFormFields(loadedFields);
        setSavedFormSnapshot(getFormFieldsSnapshot(loadedFields));
        setHomepageIntro(loadedIntro);
        setSavedIntroSnapshot(getHomepageIntroSnapshot(loadedIntro));
        setRegistrations(registrationsResponse.data || []);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error loading admin data:', error);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const savedCount = programs.filter((program) => program.id).length;
    if (activeForumTab >= savedCount && savedCount > 0) {
      setActiveForumTab(0);
    }
  }, [programs, activeForumTab]);

  const showSuccessMessage = useCallback((message) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(''), 3000);
  }, []);

  const handleProgramsSaved = useCallback((message, savedPrograms) => {
    if (savedPrograms) {
      setSavedProgramsSnapshot(getProgramsSnapshot(savedPrograms));
    }
    showSuccessMessage(message);
  }, [showSuccessMessage]);

  const handleIntroSaved = useCallback((message, savedIntro) => {
    if (savedIntro) {
      setSavedIntroSnapshot(getHomepageIntroSnapshot(savedIntro));
    }
    showSuccessMessage(message);
  }, [showSuccessMessage]);

  const handleFormSaved = useCallback((message, savedFields) => {
    if (savedFields) {
      setSavedFormSnapshot(getFormFieldsSnapshot(savedFields));
    }
    showSuccessMessage(message);
  }, [showSuccessMessage]);

  const handleTogglePrograms = () => {
    if (isTogglingPrograms) return;

    const nextEnabled = !programsEnabled;
    setIsTogglingPrograms(true);

    axios.post('/api/programs-status', { isEnabled: nextEnabled })
      .then(() => {
        setProgramsEnabled(nextEnabled);
        showSuccessMessage(
          nextEnabled
            ? 'Программы снова видны на главной и в регистрации'
            : 'Программы скрыты на главной, поле «Форум» убрано из регистрации'
        );
      })
      .catch((error) => {
        console.error('Error updating programs status:', error);
        alert('Ошибка при изменении статуса программ');
      })
      .finally(() => {
        setIsTogglingPrograms(false);
      });
  };

  const hasUnsavedProgramChanges = useMemo(
    () => savedProgramsSnapshot !== getProgramsSnapshot(programs),
    [programs, savedProgramsSnapshot]
  );

  const hasUnsavedFormChanges = useMemo(
    () => savedFormSnapshot !== getFormFieldsSnapshot(formFields),
    [formFields, savedFormSnapshot]
  );

  const hasUnsavedIntroChanges = useMemo(
    () => savedIntroSnapshot !== getHomepageIntroSnapshot(homepageIntro),
    [homepageIntro, savedIntroSnapshot]
  );

  const hasUnsavedChanges = hasUnsavedProgramChanges || hasUnsavedFormChanges || hasUnsavedIntroChanges;

  const proceedLeave = async (action) => {
    setLeaveConfirmAction(null);

    if (action === 'home') {
      navigate('/');
      return;
    }

    await logout();
    navigate('/', { replace: true });
  };

  const requestLeave = (action) => {
    if (hasUnsavedChanges) {
      setLeaveConfirmAction(action);
      return;
    }

    proceedLeave(action);
  };

  const handleLogout = () => {
    requestLeave('logout');
  };

  const handleGoHome = () => {
    requestLeave('home');
  };

  const savedPrograms = programs.filter((program) => program.id);
  const hasMultipleForums = savedPrograms.length > 1;
  const showForumGrouping = programsEnabled && hasMultipleForums;
  const activeProgram = savedPrograms[activeForumTab] || savedPrograms[0] || null;
  const participantFields = programsEnabled
    ? formFields
    : formFields.filter((field) => field.type !== 'forum');

  const handleDownloadCSV = (rows, filenameLabel) => {
    downloadRegistrationsExcel(rows, `${sanitizeFilename(filenameLabel)}.csv`, participantFields);
  };

  const getRegistrationsForProgram = (programId, includeLegacy = false) => {
    if (!programId) {
      return registrations;
    }

    return registrations.filter((registration) => {
      if (registration.programSectionId === programId) {
        return true;
      }

      return includeLegacy && (registration.programSectionId == null);
    });
  };

  const visibleRegistrations = !programsEnabled
    ? registrations
    : activeProgram
      ? getRegistrationsForProgram(activeProgram.id, !hasMultipleForums)
      : registrations;

  const registrationStatsByForum = savedPrograms.map((program, index) => ({
    id: program.id,
    label: getProgramTabLabel(program, index),
    count: getRegistrationsForProgram(program.id, !hasMultipleForums).length
  }));

  const unassignedRegistrationsCount = hasMultipleForums
    ? registrations.filter((registration) => registration.programSectionId == null).length
    : 0;

  const totalScheduleRows = savedPrograms.reduce(
    (sum, program) => sum + (program.events?.length || 0),
    0
  );

  const programsWithTitleCount = savedPrograms.filter((program) => program.title?.trim()).length;

  const handleCompleteRegistration = () => {
    setIsRegistrationCompleted(true)
      .then(() => {
        setIsRegistrationCompleteClicked(true);
        setIsPopupVisible(true);
        setTimeout(() => setIsPopupVisible(false), 1500);
      })
      .catch((error) => {
        console.error('Error updating registration status:', error);
        alert('Ошибка при завершении регистрации');
      });
  };

  const handleReopenRegistration = () => {
    setIsRegistrationCompleted(false)
      .then(() => {
        setIsRegistrationReopenClicked(true);
        setIsReopenPopupVisible(true);
        setTimeout(() => setIsReopenPopupVisible(false), 1500);
      })
      .catch((error) => {
        console.error('Error updating registration status:', error);
        alert('Ошибка при открытии регистрации');
      });
  };

  const handleClearRegistrations = () => {
    if (window.confirm(
      programsEnabled
        ? 'Вы уверены, что хотите очистить базу регистраций по всем форумам? Это действие нельзя отменить.'
        : 'Вы уверены, что хотите очистить базу регистраций? Это действие нельзя отменить.'
    )) {
      axios.delete('/api/registrations/clear')
        .then((response) => {
          showSuccessMessage(`База очищена. Удалено ${response.data.clearedCount} записей.`);
          setRegistrations([]);
        })
        .catch((error) => {
          console.error('Error clearing registrations:', error);
          alert('Ошибка при очистке базы регистраций');
        });
    }
  };

  const toggleSection = (section) => {
    setActiveSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (isLoading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div className="loading-spinner" />
          <p>Загрузка панели...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-topbar">
        <div className="admin-topbar__brand">
          <h1>Админ-панель</h1>
          <p>Управление программами и регистрацией участников</p>
        </div>
        <div className="admin-topbar__actions">
          <button type="button" className="admin-link-btn" onClick={handleGoHome}>
            На главную
          </button>
          <button type="button" className="admin-logout-btn" onClick={handleLogout}>
            Выйти
          </button>
        </div>
      </header>

      <div className="admin-stats">
        <div className="admin-stat-card">
          <span className="admin-stat-card__label">Регистрация</span>
          <span className={`admin-status-badge ${isRegistrationCompleted ? 'is-closed' : 'is-open'}`}>
            {isRegistrationCompleted ? 'Закрыта' : 'Открыта'}
          </span>
        </div>
        <div className="admin-stat-card admin-stat-card--participants">
          <span className="admin-stat-card__label">Участников</span>
          <strong className="admin-stat-card__value">{registrations.length}</strong>
          <p className="admin-stat-card__summary">
            {!programsEnabled
              ? 'Общий список без привязки к программе'
              : savedPrograms.length > 1
                ? `Распределены на ${savedPrograms.length} ${pluralForums(savedPrograms.length)}`
                : savedPrograms.length === 1
                  ? `На форум «${getProgramTabLabel(savedPrograms[0], 0)}»`
                  : 'Форумы ещё не настроены'}
          </p>
          {programsEnabled && savedPrograms.length > 0 ? (
            <ul className="admin-stat-card__breakdown">
              {registrationStatsByForum.map((forumStat) => (
                <li key={forumStat.id}>
                  <span className="admin-stat-card__breakdown-label">{forumStat.label}</span>
                  <span className="admin-stat-card__breakdown-value">{forumStat.count}</span>
                </li>
              ))}
              {unassignedRegistrationsCount > 0 && (
                <li>
                  <span className="admin-stat-card__breakdown-label">Без форума</span>
                  <span className="admin-stat-card__breakdown-value">{unassignedRegistrationsCount}</span>
                </li>
              )}
            </ul>
          ) : (
            <p className="admin-stat-card__hint">
              {programsEnabled
                ? 'Добавьте программу, чтобы принимать регистрации'
                : 'Программы отключены, заявки идут в общий список'}
            </p>
          )}
        </div>
        <div className="admin-stat-card admin-stat-card--programs">
          <span className="admin-stat-card__label">Программ на сайте</span>
          <strong className="admin-stat-card__value">{savedPrograms.length}</strong>
          <p className="admin-stat-card__summary">
            {programsEnabled
              ? `${savedPrograms.length} ${pluralForums(savedPrograms.length)} для регистрации`
              : 'Скрыты на сайте, в админке сохранены'}
            {programsEnabled && totalScheduleRows > 0 && ` · ${totalScheduleRows} ${pluralScheduleRows(totalScheduleRows)} в расписании`}
          </p>
          {savedPrograms.length > 0 ? (
            <ul className="admin-stat-card__breakdown">
              {savedPrograms.map((program, index) => (
                <li key={program.id}>
                  <span className="admin-stat-card__breakdown-label">{getProgramTabLabel(program, index)}</span>
                  <span className="admin-stat-card__breakdown-value">
                    {program.events?.length || 0} {pluralScheduleRows(program.events?.length || 0)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="admin-stat-card__hint">Пока нет опубликованных программ</p>
          )}
          {savedPrograms.length > 0 && programsWithTitleCount < savedPrograms.length && (
            <p className="admin-stat-card__hint">
              {savedPrograms.length - programsWithTitleCount} без заглавия
            </p>
          )}
        </div>
      </div>

      <section className="admin-section-card admin-section-card--highlight">
        <div className="admin-section-card__body admin-section-card__body--flat">
          <div className="registration-controls">
            <div className="registration-controls__info">
              <h2>Управление регистрацией</h2>
              <p>
                {isRegistrationCompleted
                  ? 'Форма регистрации скрыта для посетителей сайта.'
                  : 'Посетители могут регистрироваться через форму на сайте.'}
              </p>
            </div>
            {isRegistrationCompleted ? (
              <button
                type="button"
                onClick={handleReopenRegistration}
                className={`admin-action-btn admin-action-btn--success reopen-registration-btn ${isRegistrationReopenClicked ? 'clicked' : ''}`}
              >
                Открыть регистрацию
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCompleteRegistration}
                className={`admin-action-btn admin-action-btn--danger complete-registration-btn ${isRegistrationCompleteClicked ? 'clicked' : ''}`}
              >
                Завершить регистрацию
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="admin-section-card">
        <SectionHeader
          title="Текст на главной"
          subtitle="Заголовок и два дополнительных текста под ним"
          isOpen={activeSections.intro}
          onToggle={() => toggleSection('intro')}
        />
        <CollapsibleSectionBody isOpen={activeSections.intro}>
          <HomepageIntroEditor
            intro={homepageIntro}
            setIntro={setHomepageIntro}
            onSuccess={handleIntroSaved}
          />
        </CollapsibleSectionBody>
      </section>

      <section className="admin-section-card">
        <SectionHeader
          title="Программы"
          subtitle={programsEnabled
            ? 'Баннер и таблица для главной страницы'
            : 'Скрыты на главной и в форме регистрации'}
          isOpen={activeSections.programs}
          onToggle={() => toggleSection('programs')}
          actions={(
            <button
              type="button"
              className={`admin-action-btn admin-section-card__programs-toggle ${programsEnabled ? 'admin-action-btn--ghost-danger' : 'admin-action-btn--success'}`}
              onClick={handleTogglePrograms}
              disabled={isTogglingPrograms}
            >
              {programsEnabled ? 'Отключить программы' : 'Включить программы'}
            </button>
          )}
        />
        <CollapsibleSectionBody isOpen={activeSections.programs}>
          <ProgramDocumentsEditor
            programs={programs}
            setPrograms={setPrograms}
            onSuccess={handleProgramsSaved}
          />
        </CollapsibleSectionBody>
      </section>

      <section className="admin-section-card">
        <SectionHeader
          title="Форма регистрации"
          subtitle={`${formFields.length} полей · порядок как на сайте`}
          isOpen={activeSections.form}
          onToggle={() => toggleSection('form')}
        />
        <CollapsibleSectionBody isOpen={activeSections.form}>
          <RegistrationFormEditor
            fields={formFields}
            setFields={setFormFields}
            onSuccess={handleFormSaved}
          />
        </CollapsibleSectionBody>
      </section>

      <section className="admin-section-card">
        <SectionHeader
          title="Участники"
          subtitle={`Зарегистрировано: ${registrations.length}`}
          isOpen={activeSections.registrations}
          onToggle={() => toggleSection('registrations')}
        />
        <CollapsibleSectionBody isOpen={activeSections.registrations}>
          <div className="admin-toolbar">
            {!showForumGrouping && (
              <button
                type="button"
                className="admin-action-btn admin-action-btn--success"
                onClick={() => handleDownloadCSV(
                  visibleRegistrations,
                  programsEnabled && activeProgram
                    ? getProgramTabLabel(activeProgram, 0)
                    : 'bez-programmy'
                )}
              >
                Скачать Excel — {programsEnabled && activeProgram
                  ? getProgramTabLabel(activeProgram, 0)
                  : 'Без программы'}
              </button>
            )}
            <button type="button" className="admin-action-btn admin-action-btn--danger" onClick={handleClearRegistrations}>
              {programsEnabled ? 'Очистить базу (все форумы)' : 'Очистить базу'}
            </button>
          </div>

          {showForumGrouping && (
            <div className="registration-forum-tabs-scroll">
              <div className="registration-forum-tabs" role="tablist" aria-label="Форумы участников">
                {savedPrograms.map((program, index) => {
                  const count = getRegistrationsForProgram(program.id).length;
                  return (
                    <button
                      key={program.id}
                      type="button"
                      role="tab"
                      aria-selected={index === activeForumTab}
                      className={`registration-forum-tab ${index === activeForumTab ? 'is-active' : ''}`}
                      onClick={() => setActiveForumTab(index)}
                    >
                      <span className="registration-forum-tab__label">{getProgramTabLabel(program, index)}</span>
                      <span className="registration-forum-tab__count">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {showForumGrouping && activeProgram ? (
            <RegistrationsForumPanel
              program={activeProgram}
              programIndex={activeForumTab}
              registrations={visibleRegistrations}
              fields={participantFields}
            />
          ) : (
            <RegistrationsTable
              registrations={visibleRegistrations}
              fields={participantFields}
              emptyMessage={
                programsEnabled
                  ? 'Пока никто не зарегистрировался на этот форум'
                  : 'Пока никто не зарегистрировался'
              }
            />
          )}
        </CollapsibleSectionBody>
      </section>

      <AnimatePresence>
        {leaveConfirmAction && (
          <motion.div
            className="admin-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLeaveConfirmAction(null)}
          >
            <motion.div
              className="admin-modal admin-modal--confirm"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="admin-modal__icon admin-modal__icon--warning">!</div>
              <h3>Несохранённые изменения</h3>
              <p>
                Есть несохранённые изменения в тексте на главной, программах или форме регистрации.
                Если уйти сейчас, они будут потеряны.
              </p>
              <div className="admin-modal__actions">
                <button
                  type="button"
                  className="admin-action-btn admin-action-btn--primary"
                  onClick={() => setLeaveConfirmAction(null)}
                >
                  Остаться
                </button>
                <button
                  type="button"
                  className="admin-action-btn admin-action-btn--ghost-danger"
                  onClick={() => proceedLeave(leaveConfirmAction)}
                >
                  Уйти без сохранения
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isPopupVisible && (
          <motion.div className="admin-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="admin-modal" initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}>
              <div className="admin-modal__icon admin-modal__icon--warning">!</div>
              <h3>Регистрация завершена</h3>
              <p>Новые участники больше не смогут зарегистрироваться.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isReopenPopupVisible && (
          <motion.div className="admin-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="admin-modal" initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}>
              <div className="admin-modal__icon admin-modal__icon--success">✓</div>
              <h3>Регистрация открыта</h3>
              <p>Форма снова доступна на сайте.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toastMessage && (
          <motion.div
            className="admin-toast admin-toast--success"
            initial={{ opacity: 0, y: -16, x: 16 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, y: -16, x: 16 }}
          >
            {toastMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPanel;
